import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { API_BASE } from '../../config/api';

export const SystemLogs = () => {
  const navigate = useNavigate();
  const { logId } = useParams();
  const [logs, setLogs] = useState([]);
  const [selectedLog, setSelectedLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Deployment status
  const [deploymentStatus, setDeploymentStatus] = useState({
    frontend: { status: 'unknown', message: 'Checking...', url: '' },
    backend: { status: 'unknown', message: 'Checking...', url: '' }
  });
  const [statusLoading, setStatusLoading] = useState(true);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 50;
  
  // Filters
  const [filterSeverity, setFilterSeverity] = useState('');
  const [filterDomain, setFilterDomain] = useState('');
  const [filterRoute, setFilterRoute] = useState('');
  const [filterApiEndpoint, setFilterApiEndpoint] = useState('');
  const [filterCorrelationId, setFilterCorrelationId] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  
  const backendUrl = API_BASE;

  // Fetch deployment status
  const fetchDeploymentStatus = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) return;

      const response = await fetch(`${backendUrl}/api/admin/deployment-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.status) {
          setDeploymentStatus(data.status);
        }
      }
    } catch (err) {
      console.error('Failed to fetch deployment status:', err);
    } finally {
      setStatusLoading(false);
    }
  };

  // Fetch deployment status on mount and every 30 seconds
  useEffect(() => {
    if (!logId) {
      fetchDeploymentStatus();
      const interval = setInterval(fetchDeploymentStatus, 30000); // Refresh every 30 seconds
      return () => clearInterval(interval);
    }
  }, [logId]);

  useEffect(() => {
    if (logId) {
      fetchLogDetail(logId);
    } else {
      fetchSystemLogs();
    }
  }, [currentPage, filterSeverity, filterDomain, filterRoute, filterApiEndpoint, filterCorrelationId, filterDateFrom, filterDateTo, logId]);

  const fetchSystemLogs = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('admin_token');
      
      if (!token) {
        navigate('/admin/login');
        return;
      }

      // Build query params
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: limit.toString()
      });
      
      if (filterSeverity) params.append('severity', filterSeverity);
      if (filterDomain) params.append('domain', filterDomain);
      if (filterRoute) params.append('route', filterRoute);
      if (filterApiEndpoint) params.append('api_endpoint', filterApiEndpoint);
      if (filterCorrelationId) params.append('correlation_id', filterCorrelationId);
      if (filterDateFrom) params.append('date_from', filterDateFrom);
      if (filterDateTo) params.append('date_to', filterDateTo);

      const response = await fetch(`${backendUrl}/api/admin/system-logs?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
        return;
      }

      if (response.status === 403) {
        setError('Access denied. Only Super Admin and Tech roles can view system logs.');
        setLoading(false);
        return;
      }

      if (!response.ok) throw new Error('Failed to fetch system logs');

      const data = await response.json();
      setLogs(data.logs || []);
      setTotalPages(data.pagination?.total_pages || 1);
      setTotal(data.pagination?.total || 0);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to load system logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchLogDetail = async (id) => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('admin_token');
      
      if (!token) {
        navigate('/admin/login');
        return;
      }

      const response = await fetch(`${backendUrl}/api/admin/system-logs/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
        return;
      }

      if (response.status === 403) {
        setError('Access denied. Only Super Admin and Tech roles can view system logs.');
        setLoading(false);
        return;
      }

      if (!response.ok) throw new Error('Failed to fetch log detail');

      const data = await response.json();
      setSelectedLog(data.log);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to load log detail');
    } finally {
      setLoading(false);
    }
  };

  const handleResetFilters = () => {
    setFilterSeverity('');
    setFilterDomain('');
    setFilterRoute('');
    setFilterApiEndpoint('');
    setFilterCorrelationId('');
    setFilterDateFrom('');
    setFilterDateTo('');
    setCurrentPage(1);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'error':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'info':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'audit':
        return 'bg-green-100 text-green-800 border-green-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Copied to clipboard!');
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  };

  // If viewing a single log detail
  if (logId && selectedLog) {
    return (
      <AdminLayout>
        <div className="space-y-6">
          <div>
            <button
              onClick={() => navigate('/admin/system-logs')}
              className="text-[#1E40AF] hover:text-[#1e3a8a] font-semibold text-sm flex items-center gap-2 mb-3"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to System Logs
            </button>
            <h1 className="text-3xl font-bold text-[#111827]">Log Detail</h1>
          </div>

          <div className="bg-white border-2 border-[#E5E7EB] rounded-xl p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-semibold text-[#6B7280]">Log ID</label>
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-sm text-[#111827] font-mono">{selectedLog.log_id}</p>
                  <button
                    onClick={() => copyToClipboard(selectedLog.log_id)}
                    className="text-[#1E40AF] hover:text-[#1e3a8a] text-xs"
                  >
                    Copy
                  </button>
                </div>
              </div>
              <div>
                <label className="text-sm font-semibold text-[#6B7280]">Correlation ID</label>
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-sm text-[#111827] font-mono">{selectedLog.correlation_id}</p>
                  <button
                    onClick={() => copyToClipboard(selectedLog.correlation_id)}
                    className="text-[#1E40AF] hover:text-[#1e3a8a] text-xs"
                  >
                    Copy
                  </button>
                </div>
              </div>
              <div>
                <label className="text-sm font-semibold text-[#6B7280]">Severity</label>
                <span className={`inline-block px-3 py-1 rounded-lg text-xs font-semibold border-2 mt-1 ${getSeverityColor(selectedLog.severity)}`}>
                  {selectedLog.severity.toUpperCase()}
                </span>
              </div>
              <div>
                <label className="text-sm font-semibold text-[#6B7280]">Domain</label>
                <p className="text-sm text-[#111827] mt-1">{selectedLog.domain}</p>
              </div>
              <div>
                <label className="text-sm font-semibold text-[#6B7280]">Timestamp</label>
                <p className="text-sm text-[#111827] mt-1">{formatDateTime(selectedLog.timestamp)}</p>
              </div>
              <div>
                <label className="text-sm font-semibold text-[#6B7280]">User Type</label>
                <p className="text-sm text-[#111827] mt-1">{selectedLog.user_type}</p>
              </div>
              {selectedLog.route && (
                <div>
                  <label className="text-sm font-semibold text-[#6B7280]">Route</label>
                  <p className="text-sm text-[#111827] mt-1">{selectedLog.route}</p>
                </div>
              )}
              {selectedLog.api_endpoint && (
                <div>
                  <label className="text-sm font-semibold text-[#6B7280]">API Endpoint</label>
                  <p className="text-sm text-[#111827] mt-1">{selectedLog.api_endpoint}</p>
                </div>
              )}
              {selectedLog.http_status && (
                <div>
                  <label className="text-sm font-semibold text-[#6B7280]">HTTP Status</label>
                  <p className="text-sm text-[#111827] mt-1">{selectedLog.http_status}</p>
                </div>
              )}
              {selectedLog.error_type && (
                <div>
                  <label className="text-sm font-semibold text-[#6B7280]">Error Type</label>
                  <p className="text-sm text-[#111827] mt-1">{selectedLog.error_type}</p>
                </div>
              )}
            </div>

            <div>
              <label className="text-sm font-semibold text-[#6B7280]">Message</label>
              <p className="text-sm text-[#111827] mt-1 bg-[#F8FAFC] p-3 rounded-lg">{selectedLog.message}</p>
            </div>

            {selectedLog.stack_trace && (
              <div>
                <label className="text-sm font-semibold text-[#6B7280]">Stack Trace</label>
                <pre className="text-xs text-[#111827] mt-1 bg-[#F8FAFC] p-3 rounded-lg overflow-auto max-h-96 font-mono whitespace-pre-wrap">
                  {selectedLog.stack_trace}
                </pre>
              </div>
            )}

            {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
              <div>
                <label className="text-sm font-semibold text-[#6B7280]">Metadata</label>
                <pre className="text-xs text-[#111827] mt-1 bg-[#F8FAFC] p-3 rounded-lg overflow-auto max-h-96 font-mono">
                  {JSON.stringify(selectedLog.metadata, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-[#111827]">System Logs</h1>
          <p className="text-sm text-[#6B7280] mt-2">Central logging hub for frontend and backend errors</p>
        </div>

        {/* Deployment Status Section */}
        <div className="bg-white border-2 border-[#E5E7EB] rounded-xl p-6">
          <h2 className="text-lg font-bold text-[#111827] mb-4">Deployment Status</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Frontend Status */}
            <div className="border-2 border-[#E5E7EB] rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-[#111827]">Frontend (Google Cloud)</h3>
                {statusLoading ? (
                  <div className="w-4 h-4 border-2 border-[#1E40AF] border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <span className={`inline-block w-3 h-3 rounded-full ${
                    deploymentStatus.frontend.status === 'good' ? 'bg-green-500' : 'bg-red-500'
                  }`}></span>
                )}
              </div>
              <p className={`text-sm font-semibold mb-1 ${
                deploymentStatus.frontend.status === 'good' ? 'text-green-700' : 
                deploymentStatus.frontend.status === 'error' ? 'text-red-700' : 'text-gray-700'
              }`}>
                {deploymentStatus.frontend.status === 'good' ? '✓ Good and Working' : 
                 deploymentStatus.frontend.status === 'error' ? '✗ Error or Down' : 'Checking...'}
              </p>
              <p className="text-xs text-[#6B7280]">{deploymentStatus.frontend.message}</p>
              {deploymentStatus.frontend.url && (
                <p className="text-xs text-[#6B7280] mt-1">URL: {deploymentStatus.frontend.url}</p>
              )}
              {deploymentStatus.frontend.response_time_ms !== null && (
                <p className="text-xs text-[#6B7280] mt-1">
                  Response time: {deploymentStatus.frontend.response_time_ms}ms
                </p>
              )}
            </div>

            {/* Backend Status */}
            <div className="border-2 border-[#E5E7EB] rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-[#111827]">Backend (Render)</h3>
                {statusLoading ? (
                  <div className="w-4 h-4 border-2 border-[#1E40AF] border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <span className={`inline-block w-3 h-3 rounded-full ${
                    deploymentStatus.backend.status === 'good' ? 'bg-green-500' : 'bg-red-500'
                  }`}></span>
                )}
              </div>
              <p className={`text-sm font-semibold mb-1 ${
                deploymentStatus.backend.status === 'good' ? 'text-green-700' : 
                deploymentStatus.backend.status === 'error' ? 'text-red-700' : 'text-gray-700'
              }`}>
                {deploymentStatus.backend.status === 'good' ? '✓ Good and Working' : 
                 deploymentStatus.backend.status === 'error' ? '✗ Error or Down' : 'Checking...'}
              </p>
              <p className="text-xs text-[#6B7280]">{deploymentStatus.backend.message}</p>
              {deploymentStatus.backend.url && deploymentStatus.backend.url !== 'Current server' && (
                <p className="text-xs text-[#6B7280] mt-1">URL: {deploymentStatus.backend.url}</p>
              )}
              {deploymentStatus.backend.response_time_ms !== null && deploymentStatus.backend.response_time_ms > 0 && (
                <p className="text-xs text-[#6B7280] mt-1">
                  Response time: {deploymentStatus.backend.response_time_ms}ms
                </p>
              )}
            </div>
          </div>
          <div className="mt-4 text-xs text-[#6B7280]">
            Status updates every 30 seconds • Last checked: {deploymentStatus.frontend.checked_at ? 
              new Date(deploymentStatus.frontend.checked_at).toLocaleTimeString() : 'Never'}
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white border-2 border-[#E5E7EB] rounded-xl p-6">
          <h2 className="text-lg font-bold text-[#111827] mb-4">Filters</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-semibold text-[#111827] mb-2">Severity</label>
              <select
                value={filterSeverity}
                onChange={(e) => setFilterSeverity(e.target.value)}
                className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
              >
                <option value="">All</option>
                <option value="error">Error</option>
                <option value="warning">Warning</option>
                <option value="info">Info</option>
                <option value="audit">Audit</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#111827] mb-2">Domain</label>
              <select
                value={filterDomain}
                onChange={(e) => setFilterDomain(e.target.value)}
                className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
              >
                <option value="">All</option>
                <option value="public">Public</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#111827] mb-2">Route</label>
              <input
                type="text"
                value={filterRoute}
                onChange={(e) => setFilterRoute(e.target.value)}
                placeholder="Filter by route..."
                className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#111827] mb-2">API Endpoint</label>
              <input
                type="text"
                value={filterApiEndpoint}
                onChange={(e) => setFilterApiEndpoint(e.target.value)}
                placeholder="Filter by endpoint..."
                className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#111827] mb-2">Correlation ID</label>
              <input
                type="text"
                value={filterCorrelationId}
                onChange={(e) => setFilterCorrelationId(e.target.value)}
                placeholder="Filter by correlation ID..."
                className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#111827] mb-2">Date From</label>
              <input
                type="datetime-local"
                value={filterDateFrom}
                onChange={(e) => setFilterDateFrom(e.target.value)}
                className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#111827] mb-2">Date To</label>
              <input
                type="datetime-local"
                value={filterDateTo}
                onChange={(e) => setFilterDateTo(e.target.value)}
                className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
              />
            </div>
          </div>

          <div className="mt-4">
            <button
              onClick={handleResetFilters}
              className="px-4 py-2 border-2 border-[#E5E7EB] text-[#6B7280] hover:border-[#1E40AF] hover:text-[#1E40AF] rounded-lg text-sm font-semibold transition-colors"
            >
              Reset Filters
            </button>
          </div>
        </div>

        {/* Logs Table */}
        {loading ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="inline-block w-12 h-12 border-4 border-[#1E40AF] border-t-transparent rounded-full animate-spin mb-4"></div>
              <p className="text-[#6B7280]">Loading logs...</p>
            </div>
          </div>
        ) : logs.length === 0 ? (
          <div className="bg-white border-2 border-[#E5E7EB] rounded-xl p-12 text-center">
            <p className="text-[#6B7280]">No logs found</p>
          </div>
        ) : (
          <div className="bg-white border-2 border-[#E5E7EB] rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#F8FAFC]">
                  <tr className="border-b-2 border-[#E5E7EB]">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#6B7280]">Timestamp</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#6B7280]">Severity</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#6B7280]">Domain</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#6B7280]">Message</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#6B7280]">Route/Endpoint</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#6B7280]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr
                      key={log.log_id}
                      className="border-b border-[#E5E7EB] hover:bg-[#F8FAFC] transition-colors"
                    >
                      <td className="py-3 px-4 text-sm text-[#6B7280]">{formatDateTime(log.timestamp)}</td>
                      <td className="py-3 px-4">
                        <span className={`inline-block px-3 py-1 rounded-lg text-xs font-semibold border-2 ${getSeverityColor(log.severity)}`}>
                          {log.severity.toUpperCase()}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-[#6B7280]">{log.domain}</td>
                      <td className="py-3 px-4 text-sm text-[#111827] max-w-md truncate" title={log.message}>
                        {log.message}
                      </td>
                      <td className="py-3 px-4 text-sm text-[#6B7280]">
                        {log.route || log.api_endpoint || '-'}
                      </td>
                      <td className="py-3 px-4">
                        <button
                          onClick={() => navigate(`/admin/system-logs/${log.log_id}`)}
                          className="text-[#1E40AF] hover:text-[#1e3a8a] font-semibold text-sm"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="border-t-2 border-[#E5E7EB] p-4 flex items-center justify-between">
                <div className="text-sm text-[#6B7280]">
                  Showing {(currentPage - 1) * limit + 1} to {Math.min(currentPage * limit, total)} of {total} logs
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-4 py-2 border-2 border-[#E5E7EB] text-[#6B7280] rounded-lg text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:border-[#1E40AF] hover:text-[#1E40AF] transition-colors"
                  >
                    Previous
                  </button>
                  <span className="px-4 py-2 text-sm text-[#6B7280]">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 border-2 border-[#E5E7EB] text-[#6B7280] rounded-lg text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:border-[#1E40AF] hover:text-[#1E40AF] transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default SystemLogs;
